import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { toast } from '../ui/use-toast';
import { useAuth } from '../../contexts/PhpAuthContext';
import { useData } from '../../contexts/DataContext';
import { apiRequest } from '../../config/api';
import { UserPlus, Edit, Save, X, Mail, Phone, Stethoscope, Calendar, Star, Upload, User, Filter, Search, ChevronLeft, ChevronRight, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';

const AdminDoctorsTab = ({ doctors, addDoctor, updateDoctor }) => {
  const { profile } = useAuth();
  const { fetchDoctors } = useData();
  const [showAddDoctor, setShowAddDoctor] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [addedByFilter, setAddedByFilter] = useState('all');
  const [creators, setCreators] = useState([]);
  
  const [newDoctor, setNewDoctor] = useState({
    name: '',
    phone: '',
    sex: '',
    email: '',
    username: '',
    password: '',
    date_of_birth: '',
    status: 'Active',
    profile_photo: null,
    description: '',
    address: '',
    specialization: '',
    experience: '',
    rating: '4.5',
    role: 'doctor'
  });
  
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const [viewMode, setViewMode] = useState('detailed'); // 'detailed' or 'list'

  // Fetch list of creators (admins/chief doctors) for filter
  useEffect(() => {
    const fetchCreators = async () => {
      try {
        const data = await apiRequest('get_creators.php');
        if (data.success) {
          setCreators(data.creators);
        }
      } catch (error) {
        console.error('Error fetching creators:', error);
      }
    };
    fetchCreators();
  }, []);

  // Filter doctors when filter changes
  useEffect(() => {
    if (addedByFilter && addedByFilter !== 'all') {
      fetchDoctors(parseInt(addedByFilter));
    } else {
      fetchDoctors(null);
    }
    // Reset to first page when filter changes
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addedByFilter]);

  // Filter doctors based on search query
  const filteredDoctors = doctors.filter(doctor => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    const nameMatch = doctor.name?.toLowerCase().includes(query);
    const phoneMatch = doctor.phone?.toLowerCase().includes(query);
    const emailMatch = doctor.email?.toLowerCase().includes(query);
    const specializationMatch = doctor.specialization?.toLowerCase().includes(query);
    return nameMatch || phoneMatch || emailMatch || specializationMatch;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredDoctors.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedDoctors = filteredDoctors.slice(startIndex, endIndex);

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of doctor list
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddDoctor = async () => {
    // Validate mandatory fields
    // Password is optional - will be auto-generated by backend
    if (!newDoctor.name || !newDoctor.phone || !newDoctor.sex || !newDoctor.username) {
      toast({
        title: 'Error',
        description: 'Please fill in all mandatory fields (Full Name, Phone, Sex, Username)',
        variant: 'destructive'
      });
      return;
    }

    try {
      const result = await addDoctor(newDoctor);
      
      if (result.error) {
        toast({
          title: 'Error',
          description: result.error.message || 'Failed to add doctor',
          variant: 'destructive'
        });
        return;
      }
      
      // Reset form
      setNewDoctor({
        name: '',
        phone: '',
        sex: '',
        email: '',
        username: '',
        password: '',
        date_of_birth: '',
        status: 'Active',
        profile_photo: null,
        description: '',
        address: '',
        specialization: '',
        experience: '',
        rating: '4.5',
        role: 'doctor'
      });
      setProfilePhotoPreview(null);
      setShowAddDoctor(false);
      
      // Refresh doctors list
      if (addedByFilter && addedByFilter !== 'all') {
        fetchDoctors(parseInt(addedByFilter));
      } else {
        fetchDoctors(null);
      }
      
      toast({
        title: 'Doctor added successfully',
        description: `${newDoctor.name} has been added to the system`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add doctor',
        variant: 'destructive'
      });
    }
  };
  
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: 'Error',
          description: 'Photo size should be less than 5MB',
          variant: 'destructive'
        });
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Error',
          description: 'Please upload an image file',
          variant: 'destructive'
        });
        return;
      }
      
      setNewDoctor({ ...newDoctor, profile_photo: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditDoctor = (doctor) => {
    // Ensure all fields are properly set, including defaults for missing values
    const doctorData = {
      id: doctor.id,
      name: doctor.name || '',
      username: doctor.username || '',
      phone: doctor.phone || '',
      sex: doctor.sex || '',
      email: doctor.email || '',
      date_of_birth: doctor.date_of_birth || '',
      status: doctor.status || 'Active',
      profile_photo: doctor.profile_photo || null,
      description: doctor.description || '',
      address: doctor.address || '',
      specialization: doctor.specialization || '',
      experience: doctor.experience || '',
      rating: doctor.rating || '4.5',
      role: doctor.role || 'doctor',
      can_view_other_patients: doctor.can_view_other_patients || false,
      can_reset_password: doctor.can_reset_password || false, // Default to false
      can_access_camp_submissions: doctor.can_access_camp_submissions || false,
      can_access_attendance: doctor.can_access_attendance || false,
      password: '' // Don't populate password field for security
    };
    
    setEditingDoctor(doctorData);
    setShowEditPassword(false); // Reset password visibility when opening edit form
    
    // Set photo preview if doctor has a profile photo
    if (doctor.profile_photo) {
      // If it's a full URL, use it directly; if it's a relative path, make it absolute
      let photoUrl = doctor.profile_photo;
      if (photoUrl && !photoUrl.startsWith('http') && !photoUrl.startsWith('data:')) {
        // It's a relative path, make it absolute
        const API_BASE_URL = window.location.protocol + '//' + window.location.hostname;
        photoUrl = API_BASE_URL + (photoUrl.startsWith('/') ? photoUrl : '/' + photoUrl);
      }
      setEditPhotoPreview(photoUrl);
    } else {
      setEditPhotoPreview(null);
    }
  };

  const handleEditPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: 'Error',
          description: 'Photo size should be less than 5MB',
          variant: 'destructive'
        });
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Error',
          description: 'Please upload an image file',
          variant: 'destructive'
        });
        return;
      }
      
      setEditingDoctor({ ...editingDoctor, profile_photo: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateDoctor = async () => {
    // Validate mandatory fields
    if (!editingDoctor.name || !editingDoctor.phone || !editingDoctor.sex) {
      toast({
        title: 'Error',
        description: 'Please fill in all mandatory fields (Full Name, Phone, Sex)',
        variant: 'destructive'
      });
      return;
    }

    try {
      const result = await updateDoctor(editingDoctor.id, editingDoctor);
      
      if (result.error) {
        toast({
          title: 'Error',
          description: result.error.message || 'Failed to update doctor',
          variant: 'destructive'
        });
        return;
      }
      
      setEditingDoctor(null);
      setEditPhotoPreview(null);
      
      // Refresh doctors list
      if (addedByFilter && addedByFilter !== 'all') {
        fetchDoctors(parseInt(addedByFilter));
      } else {
        fetchDoctors(null);
      }
      
      toast({
        title: 'Doctor updated successfully',
        description: `${editingDoctor.name}'s information has been updated`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update doctor',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Add Button and Filter */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <h2 className="text-2xl font-semibold text-white">Manage Doctors</h2>
        <div className="flex items-center space-x-3">
          {/* Filter by Added By */}
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <Select value={addedByFilter} onValueChange={setAddedByFilter}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white w-48">
                <SelectValue placeholder="Filter by Added By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Doctors</SelectItem>
                {creators
                  .filter((creator) => creator.role !== 'admin') // Exclude admin from dropdown
                  .map((creator) => (
                  <SelectItem key={creator.id} value={creator.id.toString()}>
                    {creator.full_name} ({creator.role === 'admin' ? 'Admin' : creator.role === 'chief_doctor' ? 'Chief Doctor' : creator.role === 'doctor' ? 'Doctor' : creator.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => setShowAddDoctor(true)}
            className="medical-gradient text-white hover:opacity-90"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add Doctor
          </Button>
        </div>
      </div>

      {/* Add Doctor Form */}
      {showAddDoctor && (
        <Card className="glass-effect border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Add New Doctor</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Mandatory Fields */}
              <div>
                <Label htmlFor="name" className="text-white">Full Name *</Label>
                <Input
                  id="name"
                  value={newDoctor.name}
                  onChange={(e) => setNewDoctor({ ...newDoctor, name: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Dr. John Smith"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-white">Phone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={newDoctor.phone}
                  onChange={(e) => setNewDoctor({ ...newDoctor, phone: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="+91 9629996774"
                  required
                />
              </div>
              <div>
                <Label htmlFor="sex" className="text-white">Sex *</Label>
                <Select value={newDoctor.sex} onValueChange={(value) => setNewDoctor({ ...newDoctor, sex: value })}>
                  <SelectTrigger id="sex" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Select sex" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Optional Fields */}
              <div>
                <Label htmlFor="email" className="text-white">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newDoctor.email}
                  onChange={(e) => setNewDoctor({ ...newDoctor, email: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="doctor@dentcare.com"
                />
              </div>
              <div>
                <Label htmlFor="date_of_birth" className="text-white">Date of Birth</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={newDoctor.date_of_birth}
                  onChange={(e) => setNewDoctor({ ...newDoctor, date_of_birth: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="status" className="text-white">Status</Label>
                <Select value={newDoctor.status} onValueChange={(value) => setNewDoctor({ ...newDoctor, status: value })}>
                  <SelectTrigger id="status" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="role" className="text-white">Role</Label>
                <Select value={newDoctor.role} onValueChange={(value) => setNewDoctor({ ...newDoctor, role: value })}>
                  <SelectTrigger id="role" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="doctor">Doctor</SelectItem>
                    <SelectItem value="chief_doctor">Chief Doctor</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="clinic_login">Clinic Login</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Profile Photo */}
              <div className="md:col-span-2">
                <Label htmlFor="profile_photo" className="text-white">Profile Photo</Label>
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <Input
                      id="profile_photo"
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="bg-gray-800 border-gray-600 text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600"
                    />
                  </div>
                  {profilePhotoPreview && (
                    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-600">
                      <img src={profilePhotoPreview} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">Max size: 5MB. Accepted formats: JPG, PNG, GIF</p>
              </div>
              
              {/* Username and Password */}
              <div>
                <Label htmlFor="username" className="text-white">Set Username *</Label>
                <Input
                  id="username"
                  type="text"
                  value={newDoctor.username}
                  onChange={(e) => setNewDoctor({ ...newDoctor, username: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Enter username for login"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Used for login</p>
              </div>
              <div>
                <Label htmlFor="password" className="text-white">Set Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={newDoctor.password}
                    onChange={(e) => setNewDoctor({ ...newDoctor, password: e.target.value })}
                    className="bg-gray-800 border-gray-600 text-white pr-10"
                    placeholder="Leave blank for auto-generated password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Leave blank to auto-generate: gugan + first 3 letters of name + @999</p>
              </div>
              
              {/* Address */}
              <div className="md:col-span-2">
                <Label htmlFor="address" className="text-white">Address</Label>
                <Textarea
                  id="address"
                  value={newDoctor.address}
                  onChange={(e) => setNewDoctor({ ...newDoctor, address: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Enter full address"
                  rows={3}
                />
              </div>
              
              {/* Description */}
              <div className="md:col-span-2">
                <Label htmlFor="description" className="text-white">Description</Label>
                <Textarea
                  id="description"
                  value={newDoctor.description}
                  onChange={(e) => setNewDoctor({ ...newDoctor, description: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Enter doctor's description or bio"
                  rows={4}
                />
              </div>
              
              {/* Additional Fields */}
              <div>
                <Label htmlFor="specialization" className="text-white">Specialization</Label>
                <Select value={newDoctor.specialization} onValueChange={(value) => setNewDoctor({ ...newDoctor, specialization: value })}>
                  <SelectTrigger id="specialization" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Select specialization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General Dentistry">General Dentistry</SelectItem>
                    <SelectItem value="Orthodontics">Orthodontics</SelectItem>
                    <SelectItem value="Oral Surgery">Oral Surgery</SelectItem>
                    <SelectItem value="Periodontics">Periodontics</SelectItem>
                    <SelectItem value="Endodontics">Endodontics</SelectItem>
                    <SelectItem value="Pediatric Dentistry">Pediatric Dentistry</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="experience" className="text-white">Experience</Label>
                <Input
                  id="experience"
                  value={newDoctor.experience}
                  onChange={(e) => setNewDoctor({ ...newDoctor, experience: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="5 years"
                />
              </div>
              
              {/* Added By Field (Read-only) */}
              <div className="md:col-span-2">
                <Label className="text-white">Added By</Label>
                <Input
                  value={profile ? `${profile.full_name || profile.name} (${profile.role === 'admin' ? 'Admin' : profile.role === 'chief_doctor' ? 'Chief Doctor' : profile.role})` : 'Current User'}
                  className="bg-gray-700 border-gray-600 text-gray-300 cursor-not-allowed"
                  readOnly
                  disabled
                />
                <p className="text-xs text-gray-400 mt-1">This doctor will be added by the currently logged-in user</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <Button onClick={handleAddDoctor} className="medical-gradient text-white hover:opacity-90">
                <Save className="h-4 w-4 mr-2" />
                Add Doctor
              </Button>
              <Button variant="outline" onClick={() => { setShowAddDoctor(false); setShowPassword(false); }} className="border-gray-600 text-gray-300 hover:bg-gray-700">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Doctor Form - Full Form */}
      {editingDoctor && (
        <Card className="glass-effect border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Edit Doctor</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Mandatory Fields */}
              <div>
                <Label htmlFor="edit-name" className="text-white">Full Name *</Label>
                <Input
                  id="edit-name"
                  value={editingDoctor.name || ''}
                  onChange={(e) => setEditingDoctor({ ...editingDoctor, name: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Dr. John Smith"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-phone" className="text-white">Phone *</Label>
                <Input
                  id="edit-phone"
                  type="tel"
                  value={editingDoctor.phone || ''}
                  onChange={(e) => setEditingDoctor({ ...editingDoctor, phone: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="+91 9629996774"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-sex" className="text-white">Sex *</Label>
                <Select value={editingDoctor.sex || ''} onValueChange={(value) => setEditingDoctor({ ...editingDoctor, sex: value })}>
                  <SelectTrigger id="edit-sex" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Select sex" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Optional Fields */}
              <div>
                <Label htmlFor="edit-email" className="text-white">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editingDoctor.email || ''}
                  onChange={(e) => setEditingDoctor({ ...editingDoctor, email: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="doctor@dentcare.com"
                />
              </div>
              <div>
                <Label htmlFor="edit-date_of_birth" className="text-white">Date of Birth</Label>
                <Input
                  id="edit-date_of_birth"
                  type="date"
                  value={editingDoctor.date_of_birth || ''}
                  onChange={(e) => setEditingDoctor({ ...editingDoctor, date_of_birth: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="edit-status" className="text-white">Status</Label>
                <Select value={editingDoctor.status || 'Active'} onValueChange={(value) => setEditingDoctor({ ...editingDoctor, status: value })}>
                  <SelectTrigger id="edit-status" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-role" className="text-white">Role</Label>
                <Select value={editingDoctor.role || 'doctor'} onValueChange={(value) => setEditingDoctor({ ...editingDoctor, role: value })}>
                  <SelectTrigger id="edit-role" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="doctor">Doctor</SelectItem>
                    <SelectItem value="chief_doctor">Chief Doctor</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="clinic_login">Clinic Login</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Show Other Patients Toggle - Only for Admin/Chief Doctor */}
              {(profile?.role === 'admin' || profile?.role === 'chief_doctor') && (
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-gray-600">
                    <div className="flex-1">
                      <Label htmlFor="edit-can_view_other_patients" className="text-white font-medium">
                        Show Other Patients
                      </Label>
                      <p className="text-xs text-gray-400 mt-1">
                        Allow this doctor to view patients added by other doctors and admins in their dashboard
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        id="edit-can_view_other_patients"
                        checked={editingDoctor.can_view_other_patients || false}
                        onChange={(e) => setEditingDoctor({ ...editingDoctor, can_view_other_patients: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              )}

              {/* Allow Password Reset Toggle - Only for Admin/Chief Doctor */}
              {(profile?.role === 'admin' || profile?.role === 'chief_doctor') && (
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-gray-600">
                    <div className="flex-1">
                      <Label htmlFor="edit-can_reset_password" className="text-white font-medium">
                        Allow Password Reset
                      </Label>
                      <p className="text-xs text-gray-400 mt-1">
                        Allow this user to reset their password in their profile settings
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        id="edit-can_reset_password"
                        checked={editingDoctor.can_reset_password || false}
                        onChange={(e) => setEditingDoctor({ ...editingDoctor, can_reset_password: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              )}

              {/* Allow Camp Submissions Toggle - Only for Admin/Chief Doctor */}
              {(profile?.role === 'admin' || profile?.role === 'chief_doctor') && (
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-gray-600">
                    <div className="flex-1">
                      <Label htmlFor="edit-can_access_camp_submissions" className="text-white font-medium">
                        Camp Submissions
                      </Label>
                      <p className="text-xs text-gray-400 mt-1">
                        Allow this doctor to access camp submissions in their dashboard
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        id="edit-can_access_camp_submissions"
                        checked={editingDoctor.can_access_camp_submissions || false}
                        onChange={(e) => setEditingDoctor({ ...editingDoctor, can_access_camp_submissions: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              )}

              {/* Allow Attendance Toggle - Only for Admin/Chief Doctor */}
              {(profile?.role === 'admin' || profile?.role === 'chief_doctor') && (
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-gray-600">
                    <div className="flex-1">
                      <Label htmlFor="edit-can_access_attendance" className="text-white font-medium">
                        Attendance
                      </Label>
                      <p className="text-xs text-gray-400 mt-1">
                        Allow this doctor to access attendance tracking in their dashboard
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        id="edit-can_access_attendance"
                        checked={editingDoctor.can_access_attendance || false}
                        onChange={(e) => setEditingDoctor({ ...editingDoctor, can_access_attendance: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              )}
              
              {/* Profile Photo */}
              <div className="md:col-span-2">
                <Label htmlFor="edit-profile_photo" className="text-white">Profile Photo</Label>
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <Input
                      id="edit-profile_photo"
                      type="file"
                      accept="image/*"
                      onChange={handleEditPhotoChange}
                      className="bg-gray-800 border-gray-600 text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600"
                    />
                  </div>
                  {editPhotoPreview && (
                    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-600">
                      <img 
                        src={editPhotoPreview} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // If image fails to load, hide preview
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {editingDoctor.profile_photo && !(editingDoctor.profile_photo instanceof File) 
                    ? 'Current photo shown. Upload a new file to replace it.' 
                    : 'Max size: 5MB. Accepted formats: JPG, PNG, GIF'}
                </p>
              </div>
              
              {/* Username and Password */}
              <div>
                <Label htmlFor="edit-username" className="text-white">Username</Label>
                <Input
                  id="edit-username"
                  type="text"
                  value={editingDoctor.username || ''}
                  onChange={(e) => setEditingDoctor({ ...editingDoctor, username: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Enter username for login"
                />
                <p className="text-xs text-gray-400 mt-1">Used for login</p>
              </div>
              <div>
                <Label htmlFor="edit-password" className="text-white">Password</Label>
                <div className="relative">
                  <Input
                    id="edit-password"
                    type={showEditPassword ? "text" : "password"}
                    value={editingDoctor.password || ''}
                    onChange={(e) => setEditingDoctor({ ...editingDoctor, password: e.target.value })}
                    className="bg-gray-800 border-gray-600 text-white pr-10"
                    placeholder="Leave blank to keep current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowEditPassword(!showEditPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    aria-label={showEditPassword ? "Hide password" : "Show password"}
                  >
                    {showEditPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Leave blank to keep current password</p>
              </div>
              
              {/* Address */}
              <div className="md:col-span-2">
                <Label htmlFor="edit-address" className="text-white">Address</Label>
                <Textarea
                  id="edit-address"
                  value={editingDoctor.address || ''}
                  onChange={(e) => setEditingDoctor({ ...editingDoctor, address: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Enter full address"
                  rows={3}
                />
              </div>
              
              {/* Description */}
              <div className="md:col-span-2">
                <Label htmlFor="edit-description" className="text-white">Description</Label>
                <Textarea
                  id="edit-description"
                  value={editingDoctor.description || ''}
                  onChange={(e) => setEditingDoctor({ ...editingDoctor, description: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Enter doctor's description or bio"
                  rows={4}
                />
              </div>
              
              {/* Additional Fields */}
              <div>
                <Label htmlFor="edit-specialization" className="text-white">Specialization</Label>
                <Select 
                  value={editingDoctor.specialization || ''} 
                  onValueChange={(value) => setEditingDoctor({ ...editingDoctor, specialization: value })}
                >
                  <SelectTrigger id="edit-specialization" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Select specialization">
                      {editingDoctor.specialization || 'Select specialization'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General Dentistry">General Dentistry</SelectItem>
                    <SelectItem value="Orthodontics">Orthodontics</SelectItem>
                    <SelectItem value="Oral Surgery">Oral Surgery</SelectItem>
                    <SelectItem value="Periodontics">Periodontics</SelectItem>
                    <SelectItem value="Endodontics">Endodontics</SelectItem>
                    <SelectItem value="Pediatric Dentistry">Pediatric Dentistry</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-experience" className="text-white">Experience</Label>
                <Input
                  id="edit-experience"
                  value={editingDoctor.experience || ''}
                  onChange={(e) => setEditingDoctor({ ...editingDoctor, experience: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="5 years"
                />
              </div>
              <div>
                <Label htmlFor="edit-rating" className="text-white">Rating</Label>
                <Select value={editingDoctor.rating || '4.5'} onValueChange={(value) => setEditingDoctor({ ...editingDoctor, rating: value })}>
                  <SelectTrigger id="edit-rating" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="4.0">4.0</SelectItem>
                    <SelectItem value="4.1">4.1</SelectItem>
                    <SelectItem value="4.2">4.2</SelectItem>
                    <SelectItem value="4.3">4.3</SelectItem>
                    <SelectItem value="4.4">4.4</SelectItem>
                    <SelectItem value="4.5">4.5</SelectItem>
                    <SelectItem value="4.6">4.6</SelectItem>
                    <SelectItem value="4.7">4.7</SelectItem>
                    <SelectItem value="4.8">4.8</SelectItem>
                    <SelectItem value="4.9">4.9</SelectItem>
                    <SelectItem value="5.0">5.0</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex space-x-3">
              <Button onClick={handleUpdateDoctor} className="medical-gradient text-white hover:opacity-90">
                <Save className="h-4 w-4 mr-2" />
                Update Doctor
              </Button>
              <Button variant="outline" onClick={() => { setEditingDoctor(null); setEditPhotoPreview(null); setShowEditPassword(false); }} className="border-gray-600 text-gray-300 hover:bg-gray-700">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Bar and View Toggle */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by name, phone, email, or specialization..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-gray-800 border-gray-600 text-white"
          />
        </div>
        <div className="flex items-center gap-4">
          {searchQuery && (
            <div className="text-sm text-gray-400">
              {filteredDoctors.length} result{filteredDoctors.length !== 1 ? 's' : ''} found
            </div>
          )}
          {/* View Toggle */}
          <div className="flex items-center gap-2">
            <Label htmlFor="view-mode-doctors" className="text-white text-sm cursor-pointer">
              List View
            </Label>
            <input
              type="checkbox"
              id="view-mode-doctors"
              checked={viewMode === 'list'}
              onChange={(e) => setViewMode(e.target.checked ? 'list' : 'detailed')}
              className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 cursor-pointer"
            />
          </div>
        </div>
      </div>

      {/* Doctors List - List View */}
      {viewMode === 'list' ? (
        <div className="space-y-3">
          {paginatedDoctors.map((doctor) => (
            editingDoctor?.id === doctor.id ? null : (
              <div
                key={doctor.id}
                className="p-3 bg-gray-800 rounded-lg flex items-center justify-between gap-2 sm:gap-3 flex-wrap"
              >
                {/* Left side: Status Badge, Doctor Info */}
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  {/* Status Badge - Icon only on mobile portrait, text on larger screens */}
                  {doctor.status && (
                    <>
                      {/* Mobile Portrait: Status Icon */}
                      {doctor.status === 'Active' ? (
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 md:hidden" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 md:hidden" />
                      )}
                      {/* Desktop: Status Badge Text */}
                      <span
                        className={`hidden md:flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                          doctor.status === 'Active'
                            ? 'bg-green-600 text-green-100'
                            : 'bg-red-600 text-red-100'
                        }`}
                      >
                        {doctor.status}
                      </span>
                    </>
                  )}
                  
                  {/* Doctor Icon - Hidden on mobile portrait */}
                  <Stethoscope className="h-4 w-4 text-blue-400 flex-shrink-0 hidden md:block" />
                  
                  {/* Doctor Name */}
                  <p className="text-white font-medium truncate text-sm sm:text-base">
                    {doctor.name 
                      ? (doctor.role === 'doctor' ? `Dr. ${doctor.name}` : doctor.name)
                      : (doctor.role === 'doctor' ? 'Dr. Unknown' : 'Unknown')
                    }
                  </p>
                  
                  {/* Phone - Always visible, but simplified on mobile */}
                  {doctor.phone && (
                    <>
                      <span className="text-gray-500 hidden md:inline">•</span>
                      <div className="flex items-center text-xs sm:text-sm text-gray-400">
                        <Phone className="h-3 w-3 mr-1 flex-shrink-0 hidden md:block" />
                        <span className="truncate">{doctor.phone}</span>
                      </div>
                    </>
                  )}
                  
                  {/* Role - Hidden on mobile portrait */}
                  {doctor.role && (
                    <>
                      <span className="text-gray-500 hidden md:inline">•</span>
                      <p className="text-gray-400 text-xs sm:text-sm truncate capitalize hidden md:block">
                        {doctor.role === 'chief_doctor' ? 'Chief Doctor' : doctor.role === 'doctor' ? 'Doctor' : doctor.role}
                      </p>
                    </>
                  )}
                  
                  {/* Specialization - Hidden on mobile portrait */}
                  {doctor.specialization && (
                    <>
                      <span className="text-gray-500 hidden md:inline">•</span>
                      <div className="flex items-center text-xs sm:text-sm text-gray-400 hidden md:flex">
                        <Stethoscope className="h-3 w-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{doctor.specialization}</span>
                      </div>
                    </>
                  )}
                  
                  {/* Added By - Hidden on mobile portrait */}
                  {doctor.added_by_name && (
                    <>
                      <span className="text-gray-500 hidden md:inline">•</span>
                      <p className="text-xs sm:text-sm text-gray-400 truncate hidden md:block">
                        Added by: {doctor.added_by_name}
                        {doctor.added_by_role && (
                          <span className="ml-1">({doctor.added_by_role === 'admin' ? 'Admin' : doctor.added_by_role === 'chief_doctor' ? 'Chief Doctor' : doctor.added_by_role})</span>
                        )}
                      </p>
                    </>
                  )}
                </div>
                
                {/* Right side: Action Button - Icon only on mobile portrait */}
                <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditDoctor(doctor)}
                    className="bg-purple-600/20 border-purple-500 text-purple-300 hover:bg-purple-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                    title="Edit"
                  >
                    <Edit className="h-3 w-3 sm:mr-1" />
                    <span className="hidden sm:inline">Edit</span>
                  </Button>
                </div>
              </div>
            )
          ))}
        </div>
      ) : (
        /* Doctors List - Detailed View (Card Grid) */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {paginatedDoctors.map((doctor) => (
            <Card key={doctor.id} className="glass-effect border-gray-700">
              <CardContent className="p-6">
                {editingDoctor?.id === doctor.id ? null : (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        {/* Icon */}
                        {doctor.profile_photo ? (
                          <img 
                            src={doctor.profile_photo} 
                            alt={doctor.name || 'Doctor'}
                            className="w-12 h-12 rounded-full object-cover border-2 border-gray-600"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        {!doctor.profile_photo && (
                          <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center border-2 border-gray-600">
                            <User className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <h3 className="text-white font-semibold">
                            {doctor.name 
                              ? (doctor.role === 'doctor' ? `Dr. ${doctor.name}` : doctor.name)
                              : (doctor.role === 'doctor' ? 'Dr. Unknown' : 'Unknown')
                            }
                          </h3>
                        </div>
                      </div>
                      {/* Status */}
                      {doctor.status && (
                        <div className={`px-2 py-1 rounded text-xs ${
                          doctor.status === 'Active' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {doctor.status}
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      {/* Phone Number */}
                      {doctor.phone && (
                        <div className="flex items-center text-sm text-gray-300">
                          <Phone className="h-4 w-4 mr-2" />
                          {doctor.phone}
                        </div>
                      )}
                      
                      {/* Email (if any) */}
                      {doctor.email && (
                        <div className="flex items-center text-sm text-gray-300">
                          <Mail className="h-4 w-4 mr-2" />
                          {doctor.email}
                        </div>
                      )}
                      
                      {/* Specialization */}
                      {doctor.specialization && (
                        <div className="flex items-center text-sm text-gray-300">
                          <Stethoscope className="h-4 w-4 mr-2" />
                          {doctor.specialization}
                        </div>
                      )}
                      
                      {/* Added by */}
                      {doctor.added_by_name && (
                        <div className="flex items-center text-sm text-gray-400">
                          <User className="h-4 w-4 mr-2" />
                          Added by: {doctor.added_by_name}
                          {doctor.added_by_role && (
                            <span className="ml-1">({doctor.added_by_role === 'admin' ? 'Admin' : doctor.added_by_role === 'chief_doctor' ? 'Chief Doctor' : doctor.added_by_role})</span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditDoctor(doctor)}
                        className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {filteredDoctors.length === 0 && (
        <Card className="glass-effect border-gray-700">
          <CardContent className="p-8 text-center">
            <User className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">
              {searchQuery ? 'No doctors found matching your search' : 'No doctors found'}
            </p>
            {searchQuery && (
              <Button
                variant="outline"
                onClick={() => setSearchQuery('')}
                className="mt-4 border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Clear Search
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pagination Controls */}
      {filteredDoctors.length > recordsPerPage && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-700">
          <div className="text-sm text-gray-400">
            Showing {startIndex + 1} to {Math.min(endIndex, filteredDoctors.length)} of {filteredDoctors.length} doctors
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  className={
                    currentPage === page
                      ? 'bg-blue-600 text-white'
                      : 'border-gray-600 text-gray-300 hover:bg-gray-700'
                  }
                >
                  {page}
                </Button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDoctorsTab;